/**
 * Builds the row data for the SummaryGrid (rates table summary section).
 *
 * Produces per-plan "Monthly Premium" / "Annual Premium" rows and, when any
 * plan group contains more than one plan, plan-group-level "Total Monthly
 * Premium" / "Total Annual Premium" rows.
 *
 * The group total is placed on the group's first plan and flagged with
 * `isGroupTotal` so the renderer (and the Excel export) can span it across the
 * group's columns.
 */

import { isAlternatePlan } from "./plan-helpers";
import { computeMonthlyPremium } from "./premium-calculations";
import {
  currentSchedules,
  rateScheduleCategory,
  slotKeyWithUnit,
} from "./rateSets";

import type { BenefitPlanGroup } from "../types/models";
import type { Rate, RateCategory, RateSchedule } from "../types";

const MONTHS_PER_YEAR = 12;

// Unrounded monthly times twelve; the grid rounds cells for display (PB-717).
const annualFromMonthly = (monthlyPremium: number): number =>
  monthlyPremium * MONTHS_PER_YEAR;

export const SUMMARY_ROW_LABELS = {
  monthly: "Monthly Premium",
  annual: "Annual Premium",
  totalMonthly: "Total Monthly Premium",
  totalAnnual: "Total Annual Premium",
  renewalTotalMonthly: "Renewal Total Monthly Premium",
  renewalTotalAnnual: "Renewal Total Annual Premium",
  revisedRenewalTotalMonthly: "Revised Renewal Total Monthly Premium",
  revisedRenewalTotalAnnual: "Revised Renewal Total Annual Premium",
} as const;

/** Marks a row as a plan-group-level total so it can span the group's columns. */
export const GROUP_TOTAL_KEY = "isGroupTotal";

/**
 * Extra row-data keys carried beside a plan's own column key. `renewalKey` holds the
 * renewal-family premium shown beside the current one (revised renewal when present,
 * else renewal); `priorRenewalKey` holds the renewal premium when revised is shown, for
 * the hover; `currentTotalKey` holds the group's current total on a renewal total row.
 */
export const renewalKey = (planId: string): string => `${planId}:renewal`;
export const priorRenewalKey = (planId: string): string =>
  `${planId}:priorRenewal`;
export const currentTotalKey = (planId: string): string =>
  `${planId}:currentTotal`;

export type SummaryCellValue = string | number | boolean | null;
export type SummaryRowData = Record<string, SummaryCellValue>;

/**
 * Premium rollups for one group, fetched from the per-group premiums endpoint.
 * When supplied to {@link buildSummaryRowDataFromBackend} the per-plan premiums
 * take precedence over those embedded in the benefit-plan-group objects, letting
 * the summary refresh after a rate edit without refetching the full group payload.
 */
export interface GroupPremiumsLookup {
  /**
   * Backend group rollups. Kept to mirror the endpoint shape; the displayed
   * group total is summed from the unrounded per-plan premiums instead (PB-717),
   * so these are not read for the total rows.
   */
  totalMonthlyPremium?: number;
  totalAnnualPremium?: number;
  /** planId → premium values. */
  planPremiums: Map<
    string,
    { monthlyPremium?: number; annualPremium?: number }
  >;
}

// A plan's embedded schedules can include renewal and revised renewal sets, which are
// projections and must never add into the current premium.
const computePlanMonthlyPremium = (
  rateSchedules: RateSchedule[] | undefined,
): number => {
  const current = currentSchedules(rateSchedules);
  if (current.length === 0) {
    return 0;
  }
  return current.reduce(
    (total, schedule) => total + computeMonthlyPremium(schedule, current),
    0,
  );
};

type RenewalCategory = Extract<RateCategory, "renewal" | "revisedRenewal">;

/**
 * Renewal rate rows have no enrollment or volume of their own: premiums use the current
 * plan's. Overlays each renewal rate's numberOfLives and volume with those of the current
 * rate in the same slot, within the matching current schedule. Design and coverage
 * indicator alone can match several current schedules (separate employee and spouse
 * age-banded ones), so the schedule must also share a rate slot, which carries the tier.
 */
const withCurrentExposure = (
  renewalSchedule: RateSchedule,
  current: RateSchedule[],
): RateSchedule => {
  const renewalSlots = new Set(
    (renewalSchedule.rates ?? []).map(slotKeyWithUnit),
  );
  const currentSchedule = current.find(
    (schedule) =>
      schedule.rateDesign === renewalSchedule.rateDesign &&
      schedule.rateCoverageIndicator ===
        renewalSchedule.rateCoverageIndicator &&
      (schedule.rates ?? []).some((rate) =>
        renewalSlots.has(slotKeyWithUnit(rate)),
      ),
  );
  if (!currentSchedule) return renewalSchedule;

  const currentBySlot = new Map<string, Rate>(
    (currentSchedule.rates ?? []).map((rate) => [slotKeyWithUnit(rate), rate]),
  );
  return {
    ...renewalSchedule,
    numberOfLives: currentSchedule.numberOfLives,
    rates: (renewalSchedule.rates ?? []).map((rate) => {
      const currentRate = currentBySlot.get(slotKeyWithUnit(rate));
      return {
        ...rate,
        numberOfLives: currentRate?.numberOfLives,
        volume: currentRate?.volume,
      };
    }),
  };
};

/** Monthly premium of one renewal-family category, or undefined when the plan has none. */
const categoryMonthlyPremium = (
  schedules: RateSchedule[] | undefined,
  category: RenewalCategory,
): number | undefined => {
  const current = currentSchedules(schedules);
  const inCategory = (schedules ?? [])
    .filter((schedule) => rateScheduleCategory(schedule) === category)
    .map((schedule) => withCurrentExposure(schedule, current));
  return inCategory.length > 0
    ? computePlanMonthlyPremium(
        inCategory.map((schedule) => ({
          ...schedule,
          rateCategory: "current",
        })),
      )
    : undefined;
};

/**
 * Builds the SummaryGrid rows from the per-plan `monthlyPremium` computed by the
 * backend (ADR-026). Always returns the per-plan "Monthly Premium" / "Annual
 * Premium" rows; the plan-group-level "Total Monthly Premium" / "Total Annual
 * Premium" rows are appended only when at least one group has more than one
 * (non-alternate) plan. Annual is derived from the unrounded monthly (PB-717)
 * rather than read from the backend's `annualPremium` field, and group totals
 * are summed from the unrounded per-plan premiums (PB-717); the grid rounds every
 * cell to whole dollars for display and surfaces the exact value in its tooltip.
 *
 * Proposal plan groups (`isProposalGroup`) have no per-group premium endpoint
 * (they aren't real benefit-plan-groups), so `premiumsByGroupId` never has an
 * entry for them — falling back to the group/plan's embedded premium fields
 * would show a stale value after a rate edit, since those fields only refresh
 * on a full proposals refetch. Compute those groups from `rateSchedulesByPlanId`
 * instead, so they reflect the live rate cache.
 *
 * Real benefit-plan-groups fall back the same way whenever `premiumsByGroupId`
 * has no entry for a plan — notably while its per-group premium query is being
 * refetched right after a rate save invalidates it. The rate-schedule cache
 * already holds the edited value at that point, so this closes the gap between
 * the save landing and the premium refetch resolving instead of showing the
 * pre-edit backend number for that window.
 */
export const buildSummaryRowDataFromBackend = (
  benefitPlanGroups: BenefitPlanGroup[],
  premiumsByGroupId?: Map<string, GroupPremiumsLookup>,
  rateSchedulesByPlanId?: Map<string, RateSchedule[]>,
): SummaryRowData[] => {
  const monthlyRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.monthly,
  };
  const annualRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.annual,
  };
  const totalMonthlyRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.totalMonthly,
    [GROUP_TOTAL_KEY]: true,
  };
  const totalAnnualRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.totalAnnual,
    [GROUP_TOTAL_KEY]: true,
  };

  const renewalTotalMonthlyRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.renewalTotalMonthly,
    [GROUP_TOTAL_KEY]: true,
  };
  const renewalTotalAnnualRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.renewalTotalAnnual,
    [GROUP_TOTAL_KEY]: true,
  };
  const revisedTotalMonthlyRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.revisedRenewalTotalMonthly,
    [GROUP_TOTAL_KEY]: true,
  };
  const revisedTotalAnnualRow: SummaryRowData = {
    summaryType: SUMMARY_ROW_LABELS.revisedRenewalTotalAnnual,
    [GROUP_TOTAL_KEY]: true,
  };

  let anyGroupHasTotal = false;
  let anyGroupHasRenewalTotal = false;
  let anyGroupHasRevisedTotal = false;

  benefitPlanGroups.forEach((group) => {
    const realPlanCount = group.benefitPlans.filter(
      (plan) => !isAlternatePlan(plan),
    ).length;

    // Prefer freshly-fetched premiums (per-group endpoint) when available,
    // falling back to the premiums embedded in the group payload.
    const groupPremiums = premiumsByGroupId?.get(group.id);
    let groupTotalMonthly = 0;
    let groupTotalAnnual = 0;
    let groupShownRenewalMonthly = 0;
    let groupRenewalMonthly = 0;
    let groupHasRenewal = false;
    let groupHasRevised = false;

    group.benefitPlans.forEach((plan) => {
      totalMonthlyRow[plan.id] = null;
      totalAnnualRow[plan.id] = null;
      [
        renewalTotalMonthlyRow,
        renewalTotalAnnualRow,
        revisedTotalMonthlyRow,
        revisedTotalAnnualRow,
      ].forEach((row) => {
        row[plan.id] = null;
      });

      if (isAlternatePlan(plan)) {
        monthlyRow[plan.id] = null;
        annualRow[plan.id] = null;
        return;
      }

      let monthlyPremium: number;
      if (group.isProposalGroup) {
        // Prefer the live rate cache; fall back to the embedded snapshot if
        // the caller omitted `rateSchedulesByPlanId` or hasn't seeded this
        // plan yet, so this doesn't silently compute a premium of 0.
        monthlyPremium = computePlanMonthlyPremium(
          rateSchedulesByPlanId?.get(plan.id) ?? plan.rateSchedule,
        );
      } else {
        const planPremium = groupPremiums?.planPremiums.get(plan.id);
        if (planPremium) {
          monthlyPremium = planPremium.monthlyPremium ?? 0;
        } else {
          const schedules =
            rateSchedulesByPlanId?.get(plan.id) ?? plan.rateSchedule;
          monthlyPremium = schedules
            ? computePlanMonthlyPremium(schedules)
            : (plan.monthlyPremium ?? 0);
        }
      }
      // Derived from the monthly, not the backend's annualPremium field, so it
      // stays in step with the monthly above it after a live rate edit (PB-717).
      const annualPremium = annualFromMonthly(monthlyPremium);
      monthlyRow[plan.id] = monthlyPremium;
      annualRow[plan.id] = annualPremium;
      groupTotalMonthly += monthlyPremium;
      groupTotalAnnual += annualPremium;

      // Renewal-family categories are read from the plan's embedded schedules: the
      // rate-schedule cache above is current-only, so it never carries them.
      const renewalMonthly = categoryMonthlyPremium(
        plan.rateSchedule,
        "renewal",
      );
      const revisedMonthly = categoryMonthlyPremium(
        plan.rateSchedule,
        "revisedRenewal",
      );
      const shownMonthly = revisedMonthly ?? renewalMonthly;
      // A plan missing a period falls back revised renewal -> renewal -> current, so a
      // group total never counts it as zero.
      groupShownRenewalMonthly += shownMonthly ?? monthlyPremium;
      groupRenewalMonthly += renewalMonthly ?? monthlyPremium;
      if (shownMonthly === undefined) return;

      monthlyRow[renewalKey(plan.id)] = shownMonthly;
      annualRow[renewalKey(plan.id)] = annualFromMonthly(shownMonthly);
      if (revisedMonthly !== undefined && renewalMonthly !== undefined) {
        monthlyRow[priorRenewalKey(plan.id)] = renewalMonthly;
        annualRow[priorRenewalKey(plan.id)] = annualFromMonthly(renewalMonthly);
      }
      groupHasRenewal = true;
      groupHasRevised ||= revisedMonthly !== undefined;
    });

    // Sum the unrounded per-plan premiums, not the backend's group total (PB-717).
    if (realPlanCount > 1) {
      anyGroupHasTotal = true;
      const firstPlanId = group.benefitPlans[0].id;
      totalMonthlyRow[firstPlanId] = groupTotalMonthly;
      totalAnnualRow[firstPlanId] = groupTotalAnnual;

      if (groupHasRenewal) {
        const [monthlyTotals, annualTotals] = groupHasRevised
          ? [revisedTotalMonthlyRow, revisedTotalAnnualRow]
          : [renewalTotalMonthlyRow, renewalTotalAnnualRow];
        monthlyTotals[firstPlanId] = groupShownRenewalMonthly;
        annualTotals[firstPlanId] = annualFromMonthly(groupShownRenewalMonthly);
        monthlyTotals[currentTotalKey(firstPlanId)] = groupTotalMonthly;
        annualTotals[currentTotalKey(firstPlanId)] = groupTotalAnnual;
        if (groupHasRevised) {
          monthlyTotals[priorRenewalKey(firstPlanId)] = groupRenewalMonthly;
          annualTotals[priorRenewalKey(firstPlanId)] =
            annualFromMonthly(groupRenewalMonthly);
        }
        anyGroupHasRevisedTotal ||= groupHasRevised;
        anyGroupHasRenewalTotal ||= !groupHasRevised;
      }
    }
  });

  const rows = anyGroupHasTotal
    ? [monthlyRow, annualRow, totalMonthlyRow, totalAnnualRow]
    : [monthlyRow, annualRow];

  return [
    ...rows,
    ...(anyGroupHasRenewalTotal
      ? [renewalTotalMonthlyRow, renewalTotalAnnualRow]
      : []),
    ...(anyGroupHasRevisedTotal
      ? [revisedTotalMonthlyRow, revisedTotalAnnualRow]
      : []),
  ];
};
